import { CHAIN_CONSTANTS, SAFETY_CONFIG } from '../common/constants'

import type {
  ValidationInput,
  SolanaSigningPayload,
  SolanaTransaction,
  TestnetChain,
} from '../../types'

/**
 * Generates a signing payload for a Solana transaction.
 */
export function generateSolanaSigningPayload(
  input: ValidationInput,
): SolanaSigningPayload {
  const constants = CHAIN_CONSTANTS[input.chain]
  const testMemo = `${SAFETY_CONFIG.TEST_MEMO_PREFIX}${Date.now()}`

  const tokenMintAddress = constants.usdcContract
  const recipientAddress = SAFETY_CONFIG.BURN_ADDRESS_SOLANA
  const recipientATA = '11111111111111111111111111111112'

  const transaction: SolanaTransaction = {
    recentBlockhash: '11111111111111111111111111111111',
    feePayer: input.walletAddress,
    nonceInfo: null,
    instructions: [
      {
        keys: [],
        programId: 'ComputeBudget111111111111111111111111111111',
        data: encodeComputeUnitLimit(500000),
      },
      {
        keys: [],
        programId: 'ComputeBudget111111111111111111111111111111',
        data: encodeComputeUnitPrice(1000),
      },
      {
        keys: [
          { pubkey: input.walletAddress, isSigner: true, isWritable: true },
          { pubkey: recipientATA, isSigner: false, isWritable: true },
          { pubkey: recipientAddress, isSigner: false, isWritable: false },
          { pubkey: tokenMintAddress, isSigner: false, isWritable: false },
          {
            pubkey: '11111111111111111111111111111111',
            isSigner: false,
            isWritable: false,
          },
          {
            pubkey: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
            isSigner: false,
            isWritable: false,
          },
        ],
        programId: 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL',
        data: [1],
      },
      {
        programId: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
        keys: [
          { pubkey: input.walletAddress, isSigner: false, isWritable: true },
          { pubkey: recipientATA, isSigner: false, isWritable: true },
          { pubkey: input.walletAddress, isSigner: true, isWritable: false },
        ],
        data: encodeSPLTokenTransfer(SAFETY_CONFIG.TEST_AMOUNT),
      },
      {
        keys: [],
        programId: 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr',
        data: testMemo,
      },
    ],
    signers: [input.walletAddress],
  }

  return {
    chain: input.chain as TestnetChain.SolanaDevnet,
    walletAddress: input.walletAddress,
    payload: transaction,
  }
}

function encodeComputeUnitLimit(limit: number): number[] {
  const data = new Uint8Array(5)
  data[0] = 0
  data[1] = limit & 0xff
  data[2] = (limit >> 8) & 0xff
  data[3] = (limit >> 16) & 0xff
  data[4] = (limit >> 24) & 0xff
  return Array.from(data)
}

function encodeComputeUnitPrice(price: number): number[] {
  const data = new Uint8Array(9)
  data[0] = 3
  for (let i = 0; i < 8; i++)
    data[1 + i] = Number((BigInt(price) >> BigInt(8 * i)) & 0xffn)
  return Array.from(data)
}

function encodeSPLTokenTransfer(_amount: string): number[] {
  const data = new Uint8Array(9)
  data[0] = 3
  return Array.from(data)
}
